import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api from '../../../services/api';

const Step11Upload = () => {
    const { mockUser, refreshUser } = useOutletContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    
    // State to hold document data
    // Structure: { base64: string, fileName: string, isUploaded: boolean }
    const [documents, setDocuments] = useState({
        photo: null,
        signature: null,
        idProof: null,
        pwbd: null
    });

    // PwD Status check to show/hide 4th row
    const [isPwD, setIsPwD] = useState(false);

    // Load Data
    useEffect(() => {
        const init = async () => {
            try {
                const res = await api.get(`/mock-cuet/application-data?mockAppId=${mockUser.id}`);
                if (res.data.success) {
                    const db = res.data.data;
                    
                    setIsPwD(db.pwbd_candidate === 1);

                    // Helper to shape existing data
                    const createDocObj = (data) => data ? { base64: data, fileName: 'Uploaded Document', isUploaded: true } : null;

                    setDocuments({
                        photo: createDocObj(db.doc_passport_photo),
                        signature: createDocObj(db.doc_signature),
                        idProof: createDocObj(db.doc_id_proof),
                        pwbd: createDocObj(db.doc_pwbd_cert)
                    });
                }
            } catch (err) { console.error(err); }
        };
        init();
    }, [mockUser]);

    // Handle File Selection
    const handleFileChange = (e, docKey, minSizeKB, maxSizeKB, allowedTypes) => {
        const file = e.target.files[0];
        if (!file) return;

        // 1. Validate Type
        const fileType = file.type; // e.g. "image/jpeg"
        const validTypes = allowedTypes.map(t => {
            if(t === 'JPG') return 'image/jpeg';
            if(t === 'JPEG') return 'image/jpeg';
            if(t === 'PDF') return 'application/pdf';
            return t;
        });

        if (!validTypes.includes(fileType)) {
            alert(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
            e.target.value = null;
            return;
        }

        // 2. Validate Size
        const fileSizeKB = file.size / 1024;
        if (fileSizeKB < minSizeKB || fileSizeKB > maxSizeKB) {
            alert(`File size must be between ${minSizeKB}KB and ${maxSizeKB}KB`);
            e.target.value = null;
            return;
        }

        // 3. Read File
        const reader = new FileReader();
        reader.onloadend = () => {
            setDocuments(prev => ({
                ...prev,
                [docKey]: {
                    base64: reader.result,
                    fileName: file.name,
                    isUploaded: false // Not yet "uploaded" (simulated click needed)
                }
            }));
        };
        reader.readAsDataURL(file);
    };

    // Simulate "Upload" button click
    const handleUploadClick = (docKey) => {
        if(!documents[docKey]?.base64) return;
        
        // In a real app, this might upload individually. 
        // Here we just mark it as ready and show preview.
        setDocuments(prev => ({
            ...prev,
            [docKey]: { ...prev[docKey], isUploaded: true }
        }));
        alert("Document Uploaded Successfully");
    };

    // Handle Delete
    const handleDelete = (docKey) => {
        if(!window.confirm("Delete this document?")) return;
        setDocuments(prev => ({ ...prev, [docKey]: null }));
    };

    // Final Submit
    const handleSubmit = async () => {
        // Validate all required docs are uploaded
        if(!documents.photo?.isUploaded) { alert("Please upload Photograph"); return; }
        if(!documents.signature?.isUploaded) { alert("Please upload Signature"); return; }
        if(!documents.idProof?.isUploaded) { alert("Please upload Identity Proof"); return; }
        if(isPwD && !documents.pwbd?.isUploaded) { alert("Please upload PwD Certificate"); return; }

        setLoading(true);

        const payload = {
            doc_passport_photo: documents.photo.base64,
            doc_signature: documents.signature.base64,
            doc_id_proof: documents.idProof.base64,
            doc_pwbd_cert: documents.pwbd?.base64 || null
        };

        try {
            const res = await api.post('/mock-cuet/save-step', {
                mockAppId: mockUser.id,
                stepNumber: 11,
                stepData: payload,
                nextStepNumber: 12
            });

            if (res.data.success) {
                refreshUser(12);
                navigate('/services/counselling/dashboard/final-submit');
            }
        } catch (err) {
            alert("Error saving documents");
        } finally {
            setLoading(false);
        }
    };

    // Helper to render a row
    const renderRow = (sNo, key, label, format, min, max, types) => (
        <tr>
            <td>{sNo}</td>
            <td>
                <span style={{color:'black', fontWeight:'bold'}}>{label}</span>
            </td>
            <td>
                <b>Document Format:</b> {format}<br/>
                <b>Min Size (KB):</b> {min}<br/>
                <b>Max Size (KB):</b> {max}
            </td>
            <td>
                <input 
                    type="file" 
                    className="form-control"
                    onChange={(e) => handleFileChange(e, key, min, max, types)}
                    accept={types.map(t => t==='PDF'?'.pdf':'.jpg,.jpeg').join(',')}
                />
                <br/>
                {documents[key] && !documents[key].isUploaded && (
                    <button 
                        className="btn btn-info btn-xs" 
                        onClick={() => handleUploadClick(key)}
                    >
                        Upload
                    </button>
                )}
            </td>
            <td className="text-center">
                {documents[key]?.isUploaded && (
                    <button className="btn btn-danger btn-xs" onClick={() => handleDelete(key)}>
                        Delete
                    </button>
                )}
            </td>
            <td className="text-center">
                {documents[key]?.isUploaded && (
                    documents[key].base64.startsWith('data:image') ? (
                        <img src={documents[key].base64} alt="Preview" style={{height:'50px', border:'1px solid #ccc'}} />
                    ) : (
                        <span className="label label-success">PDF Uploaded</span>
                    )
                )}
            </td>
        </tr>
    );

    return (
        <div id="ctl00_ContentPlaceHolder1_pnlApplicationForm">
            <div className="appstatus" style={{ paddingTop: '0px', marginTop: '0px' }}>
                <h4 id="ctl00_ContentPlaceHolder1_hrDocumentUpload">दस्तावेज़ अपलोड (Document Upload)</h4>
                
                <span className="text-danger" style={{ fontWeight: '800' }}>
                    Please make sure that all submitted documents adhere to the specifications mentioned in the table below.<br />
                    कृपया सुनिश्चित करें कि सभी प्रस्तुत दस्तावेज़ नीचे दी गई तालिका में उल्लिखित विशिष्टताओं का पालन करते हैं।
                </span>
                
                <br /><br />

                <div className="table-responsive">
                    <table className="table table-bordered table-condensed table-striped">
                        <thead>
                            <tr className="bg-info">
                                <th>S.No.</th>
                                <th>Required Document</th>
                                <th>Document Specifications</th>
                                <th style={{width:'25%'}}>Upload</th>
                                <th>Action</th>
                                <th>View</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderRow(1, 'photo', 'Passport Size Photograph (with 80% face without mask)', 'JPG/JPEG', 10, 200, ['JPG', 'JPEG'])}
                            {renderRow(2, 'signature', 'Signature', 'JPG/JPEG', 4, 30, ['JPG', 'JPEG'])}
                            {renderRow(3, 'idProof', 'Identity Proof (PDF or JPG)', 'PDF/JPG', 10, 300, ['PDF', 'JPG', 'JPEG'])}
                            {isPwD && renderRow(4, 'pwbd', 'PwD Certificate', 'PDF', 50, 300, ['PDF'])}
                        </tbody>
                    </table>
                </div>

                {/* Footer Buttons */}
                <div className="row">
                    <div className="col-sm-12 text-center" style={{marginTop:'20px'}}>
                        <button type="button" className="btn btn-warning" onClick={() => navigate('/services/counselling/dashboard/photo-capture')} style={{marginRight:'10px'}}>
                            Back
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                            {loading ? 'Saving...' : 'Save & Next'}
                        </button>
                    </div>
                </div>
                <br/><br/>
            </div>
        </div>
    );
};

export default Step11Upload;